import React, { useState, useRef, useEffect, useCallback } from 'react';
import './ChatBot.css';

// ── Types ──────────────────────────────────────────────

interface Message {
  id: number;
  role: 'user' | 'bot';
  content: string;
  isEmailForm?: boolean;
}

interface FaqItem {
  question: string;
}

// ── Predefined questions (matches backend FaqEntries) ──

const SUGGESTED_QUESTIONS: FaqItem[] = [
  { question: 'Why should I sign up?' },
  { question: 'What are Credits?' },
  { question: 'How can I create an Instagram, X, TikTok, or YouTube giveaway and pick a winner?' },
  { question: 'How can I find the link to my giveaway post?' },
  { question: 'Can I add conditions and filters to Giveaways?' },
  { question: 'Can I filter who has shared my post in Stories?' },
  { question: 'Can I download a list of giveaway participants?' },
  { question: 'Can I get a payment invoice?' },
  { question: 'What is your refund policy?' },
  { question: 'Do I need to connect my Instagram account to run giveaways?' },
  { question: 'How do I reset my password?' },
  { question: 'What platforms do you support for giveaways?' },
  { question: 'How do I pick a winner?' },
  { question: 'Is my data secure?' },
  { question: 'How do I delete my account?' },
];

const GREETING = "👋 Hi! I'm the SocialsPicker assistant. How can I help you today?";

// ── Component ───────────────────────────────────────────

const ChatBot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailForm, setEmailForm] = useState({ email: '', message: '', sent: false, error: '' });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const msgIdRef = useRef(0);

  const nextId = () => ++msgIdRef.current;

  // ── Auto-scroll ──

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // ── Focus input when chat opens ──

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // ── Add message helper ──

  const addMessage = useCallback((role: 'user' | 'bot', content: string) => {
    setMessages(prev => [...prev, { id: nextId(), role, content }]);
  }, []);

  // ── Query FAQ endpoint ──

  const queryFaq = async (question: string): Promise<{ found: boolean; answer: string }> => {
    const baseUrl = process.env.REACT_APP_API_URL || '';
    const res = await fetch(`${baseUrl}/api/Chat/faq`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question }),
    });
    if (!res.ok) throw new Error('FAQ request failed');
    return res.json();
  };

  // ── Handle question (click or type) ──

  const handleQuestion = async (question: string) => {
    if (isLoading) return;
    addMessage('user', question);
    setInputValue('');
    setIsLoading(true);

    try {
      const result = await queryFaq(question);

      if (result.found) {
        addMessage('bot', result.answer);
      } else {
        // Show "not found" + email form prompt
        addMessage('bot', result.answer);
        // Add the email form as a special message
        setMessages(prev => [
          ...prev,
          { id: nextId(), role: 'bot', content: '', isEmailForm: true },
        ]);
      }
    } catch {
      addMessage('bot', "Sorry, I'm having trouble connecting. Please try again or use the email form below.");
      setMessages(prev => [
        ...prev,
        { id: nextId(), role: 'bot', content: '', isEmailForm: true },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Send contact email ──

  const handleSendEmail = async () => {
    if (!emailForm.email.trim() || !emailForm.message.trim()) {
      setEmailForm(prev => ({ ...prev, error: 'Please fill in both fields.' }));
      return;
    }

    const baseUrl = process.env.REACT_APP_API_URL || '';
    setIsLoading(true);
    setEmailForm(prev => ({ ...prev, error: '' }));

    try {
      const transcript = messages
        .filter(m => !m.isEmailForm)
        .map(m => ({ role: m.role, content: m.content }));

      const res = await fetch(`${baseUrl}/api/Chat/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailForm.email,
          message: emailForm.message,
          transcript,
        }),
      });

      if (!res.ok) throw new Error('Failed to send');
      setEmailForm(prev => ({ ...prev, sent: true, error: '' }));
    } catch {
      setEmailForm(prev => ({ ...prev, error: 'Failed to send. Please try again later.' }));
    } finally {
      setIsLoading(false);
    }
  };

  // ── Handle Enter key ──

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      handleQuestion(inputValue.trim());
    }
  };

  // ── Reset chat ──

  const handleReset = () => {
    setMessages([]);
    setEmailForm({ email: '', message: '', sent: false, error: '' });
    setInputValue('');
  };

  // ── Render ──

  return (
    <>
      {/* ── Floating Button ── */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          fixed bottom-5 right-5 z-50
          w-14 h-14 rounded-full
          bg-gradient-to-r from-primary to-secondary
          text-white shadow-lg
          flex items-center justify-center
          hover:scale-105 active:scale-95
          transition-all duration-200
          ${!isOpen ? 'chat-button-pulse' : ''}
        `}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        )}
      </button>

      {/* ── Chat Window ── */}
      {isOpen && (
        <div className="
          fixed bottom-20 right-5 z-50
          w-[350px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-7rem)]
          bg-white rounded-2xl shadow-2xl
          flex flex-col
          border border-gray-100
          chat-window-enter
        ">
          {/* Header */}
          <div className="
            flex items-center justify-between
            px-4 py-3
            bg-gradient-to-r from-primary to-secondary
            rounded-t-2xl text-white
            shrink-0
          ">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">
                SP
              </div>
              <div>
                <p className="font-semibold text-sm">SocialsPicker Support</p>
                <p className="text-xs text-white/70">We usually reply within a few hours</p>
              </div>
            </div>
            <button
              onClick={handleReset}
              className="text-white/70 hover:text-white transition-colors text-xs mr-1"
              title="Reset chat"
            >
              Reset
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 chat-scrollbar bg-gray-50/50">
            {/* Greeting */}
            {messages.length === 0 && (
              <div className="space-y-3">
                <BotBubble text={GREETING} />
                <div className="flex flex-wrap gap-2 pt-1">
                  {SUGGESTED_QUESTIONS.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => handleQuestion(q.question)}
                      disabled={isLoading}
                      className="
                        text-left text-xs px-3 py-1.5
                        border border-gray-200 rounded-full
                        hover:border-primary hover:text-primary
                        bg-white text-gray-700
                        transition-colors duration-150
                        disabled:opacity-50 disabled:cursor-not-allowed
                      "
                    >
                      {q.question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Chat messages */}
            {messages.map(msg => (
              <div key={msg.id}>
                {msg.isEmailForm ? (
                  <EmailForm
                    email={emailForm.email}
                    message={emailForm.message}
                    sent={emailForm.sent}
                    error={emailForm.error}
                    isLoading={isLoading}
                    onEmailChange={v => setEmailForm(p => ({ ...p, email: v }))}
                    onMessageChange={v => setEmailForm(p => ({ ...p, message: v }))}
                    onSend={handleSendEmail}
                  />
                ) : msg.role === 'bot' ? (
                  <BotBubble text={msg.content} />
                ) : (
                  <UserBubble text={msg.content} />
                )}
              </div>
            ))}

            {/* Typing indicator */}
            {isLoading && !emailForm.sent && (
              <div className="flex gap-1.5 px-1 py-2">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            )}

            {/* Show suggested Qs again after bot answers, but before email */}
            {messages.length > 0 && !messages.some(m => m.isEmailForm) && (
              <div className="pt-1">
                <p className="text-xs text-gray-400 mb-2">Or ask something else:</p>
                <div className="flex flex-wrap gap-1.5">
                  {SUGGESTED_QUESTIONS.filter(
                    sq => !messages.some(m => m.role === 'user' && m.content === sq.question)
                  ).slice(0, 3).map((q, i) => (
                    <button
                      key={i}
                      onClick={() => handleQuestion(q.question)}
                      disabled={isLoading}
                      className="
                        text-left text-xs px-2.5 py-1
                        border border-gray-200 rounded-full
                        hover:border-primary hover:text-primary
                        bg-white text-gray-600
                        transition-colors duration-150
                        disabled:opacity-50
                      "
                    >
                      {q.question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t border-gray-100 bg-white rounded-b-2xl shrink-0">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={messages.some(m => m.isEmailForm) ? 'Fill in the form above first...' : 'Type your question...'}
                disabled={isLoading || messages.some(m => m.isEmailForm && !emailForm.sent)}
                className="
                  flex-1 px-3 py-2
                  border border-gray-200 rounded-full
                  text-base
                  focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20
                  disabled:bg-gray-100 disabled:cursor-not-allowed
                  transition-colors
                "
              />
              <button
                onClick={() => inputValue.trim() && handleQuestion(inputValue.trim())}
                disabled={isLoading || !inputValue.trim() || messages.some(m => m.isEmailForm)}
                className="
                  w-9 h-9 rounded-full
                  bg-gradient-to-r from-primary to-secondary
                  text-white
                  flex items-center justify-center
                  hover:opacity-90 active:scale-95
                  disabled:opacity-40 disabled:cursor-not-allowed
                  transition-all shrink-0
                "
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ── Sub-components ──────────────────────────────────────

const BotBubble: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex items-start gap-2">
    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-primary to-secondary flex items-center justify-center text-white text-[10px] font-bold shrink-0 mt-0.5">
      SP
    </div>
    <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm px-3 py-2 text-sm text-gray-700 shadow-sm max-w-[85%] whitespace-pre-line">
      {text}
    </div>
  </div>
);

const UserBubble: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex items-start gap-2 justify-end">
    <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl rounded-tr-sm px-3 py-2 text-sm text-white shadow-sm max-w-[85%]">
      {text}
    </div>
  </div>
);

// ── Email Form (in-chat) ────────────────────────────────

interface EmailFormProps {
  email: string;
  message: string;
  sent: boolean;
  error: string;
  isLoading: boolean;
  onEmailChange: (v: string) => void;
  onMessageChange: (v: string) => void;
  onSend: () => void;
}

const EmailForm: React.FC<EmailFormProps> = ({
  email, message, sent, error, isLoading,
  onEmailChange, onMessageChange, onSend,
}) => {
  if (sent) {
    return (
      <BotBubble text="✅ Thanks! Your message has been sent. We'll get back to you at the email you provided. In the meantime, feel free to ask more questions!" />
    );
  }

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-3 space-y-2.5 shadow-sm">
      <p className="text-sm font-medium text-gray-700">📧 Leave your email and we'll get back to you:</p>
      <input
        type="email"
        value={email}
        onChange={e => onEmailChange(e.target.value)}
        placeholder="your@email.com"
        disabled={isLoading}
        className="
          w-full px-3 py-2 text-base
          border border-gray-200 rounded-lg
          focus:outline-none focus:border-primary
          disabled:bg-gray-100
        "
      />
      <textarea
        value={message}
        onChange={e => onMessageChange(e.target.value)}
        placeholder="Describe your issue..."
        rows={3}
        disabled={isLoading}
        className="
          w-full px-3 py-2 text-base
          border border-gray-200 rounded-lg
          focus:outline-none focus:border-primary
          resize-none
          disabled:bg-gray-100
        "
      />
      {error && <p className="text-xs text-error">{error}</p>}
      <button
        onClick={onSend}
        disabled={isLoading || !email.trim() || !message.trim()}
        className="
          w-full py-2 rounded-lg text-sm font-medium
          bg-gradient-to-r from-primary to-secondary
          text-white
          hover:opacity-90 active:scale-[0.98]
          disabled:opacity-40 disabled:cursor-not-allowed
          transition-all
        "
      >
        {isLoading ? 'Sending...' : 'Send Message'}
      </button>
    </div>
  );
};

export default ChatBot;
